import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Outlet, Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2, LogOut, MapPin, MessageSquare, Plus, Search, UserCircle } from 'lucide-react'

import { LegalFooter } from './LegalFooter'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { AuthModal } from '../AuthModal'
import { CreateLocationModal } from '../CreateLocationModal'
import { apiFetch, DISCOURSE_URL, useAuthStatus, useSearch, useTaxonomy } from '../../shared/api/client'
import type { SearchResult, TaxonomyTerm } from '../../shared/api/types'
import { navigateToExternal } from '../../shared/lib/browser-navigation'
import { trackEvent } from '../../shared/lib/analytics'
import { buildSearchParams } from '../../shared/lib/search-params'

function SearchSuggestionItem({
  item,
  onSelect,
}: {
  item: SearchResult
  onSelect: (item: SearchResult) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className="flex w-full items-start justify-between gap-3 px-3 py-2.5 text-left transition hover:bg-emerald-50"
    >
      <div>
        <p className="text-sm font-semibold text-slate-900">{item.title}</p>
        {item.subtitle ? <p className="mt-1 text-xs text-slate-500">{item.subtitle}</p> : null}
      </div>
      <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
        {item.type === 'location' ? 'Lokacija' : 'Regija'}
      </span>
    </button>
  )
}

export function MainLayout() {
  const routeLocation = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const authStatus = useAuthStatus()
  const warningTerms = useTaxonomy('warning')
  const [searchInput, setSearchInput] = useState(searchParams.get('q') ?? '')
  const [regionInput, setRegionInput] = useState(searchParams.get('region') ?? '')
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const [isCreateLocationModalOpen, setIsCreateLocationModalOpen] = useState(false)
  const [postAuthRedirect, setPostAuthRedirect] = useState<null | 'forum'>(null)

  const isHomePage = routeLocation.pathname === '/'
  const queryValue = searchParams.get('q') ?? ''
  const regionValue = searchParams.get('region') ?? ''
  const warningValue = searchParams.get('warning') ?? ''
  const trimmedSearchInput = searchInput.trim()
  const searchSuggestions = useSearch(trimmedSearchInput, regionInput.trim() || undefined)
  const showSearchSuggestions = isSearchFocused && trimmedSearchInput.length >= 2
  const suggestionResults = useMemo(
    () => searchSuggestions.data?.results ?? [],
    [searchSuggestions.data],
  )

  useEffect(() => {
    setSearchInput(queryValue)
  }, [queryValue])

  useEffect(() => {
    setRegionInput(regionValue)
  }, [regionValue])

  const applySearchParams = (
    updates: Record<string, string>,
    options?: {
      replace?: boolean
    },
  ) => {
    const next = buildSearchParams(searchParams, updates)

    if (isHomePage) {
      setSearchParams(next, { replace: options?.replace })
      return
    }

    navigate(
      {
        pathname: '/',
        search: next.toString() ? `?${next.toString()}` : '',
      },
      { replace: options?.replace },
    )
  }

  const logout = useMutation({
    mutationFn: () => apiFetch('/auth/logout', { method: 'POST', body: '{}' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['auth-status'] })
    },
  })

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSearchFocused(false)
    trackEvent('search.submitted', {
      page: routeLocation.pathname,
      query: searchInput.trim(),
      region: regionInput.trim(),
      warning: warningValue,
    })
    applySearchParams({
      q: searchInput,
      region: regionInput,
    })
  }

  const handleSearchSuggestionSelect = (item: SearchResult) => {
    setIsSearchFocused(false)

    if (item.type === 'location' && item.slug) {
      navigate(`/locations/${item.slug}`)
      return
    }

    applySearchParams({
      q: searchInput,
      region: item.region_code ?? regionInput,
    })
  }

  const handleAddLocationClick = () => {
    if (!authStatus.data?.authenticated) {
      setPostAuthRedirect(null)
      setIsAuthModalOpen(true)
      return
    }

    setIsCreateLocationModalOpen(true)
  }

  const startForumFlow = () => {
    navigateToExternal(`${DISCOURSE_URL}/session/sso?return_path=${encodeURIComponent('/')}`)
  }

  const handleForumClick = () => {
    if (!authStatus.data?.authenticated) {
      setPostAuthRedirect('forum')
      setIsAuthModalOpen(true)
      return
    }

    startForumFlow()
  }

  const handleAuthModalClose = () => {
    setIsAuthModalOpen(false)
    setPostAuthRedirect(null)
  }

  const handleAuthSuccess = () => {
    if (postAuthRedirect === 'forum') {
      setPostAuthRedirect(null)
      startForumFlow()
    }
  }

  return (
    <div className="flex h-screen w-full flex-col bg-slate-50">
      <header className="relative z-[500] border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:px-6">
          <div className="flex w-full items-center justify-between md:w-auto">
            <div className="flex shrink-0 items-center gap-3">
              <Link to="/" className="flex shrink-0 items-center gap-2.5 md:gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-[18px] bg-emerald-100 text-emerald-700 md:h-11 md:w-11 md:rounded-[20px]">
                  <MapPin size={20} className="md:h-[22px] md:w-[22px]" />
                </div>
                <div>
                  <p className="hidden text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700 sm:block">
                    Map First
                  </p>
                  <p className="text-base font-semibold tracking-tight text-slate-900 md:text-lg">
                    Fukodrom
                  </p>
                </div>
              </Link>

              <div className="hidden xl:block xl:border-l xl:border-slate-200 xl:pl-3">
                <div className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-800">
                  Community map
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 md:hidden">
              <button
                type="button"
                aria-label="Odpri forum"
                onClick={handleForumClick}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                <MessageSquare size={18} />
              </button>

              <button
                type="button"
                aria-label="Odpri obrazec za lokacijo"
                onClick={handleAddLocationClick}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-700 text-white shadow-sm transition hover:bg-emerald-800"
              >
                <Plus size={20} />
              </button>

              {authStatus.data?.authenticated ? (
                <button
                  type="button"
                  aria-label="Odjava"
                  onClick={() => logout.mutate()}
                  disabled={logout.isPending}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {logout.isPending ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <LogOut size={18} />
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  aria-label="Odpri prijavo"
                  onClick={() => {
                    setPostAuthRedirect(null)
                    setIsAuthModalOpen(true)
                  }}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50"
                >
                  <UserCircle size={20} />
                </button>
              )}
            </div>
          </div>

          <form
            onSubmit={handleSearchSubmit}
            className="flex w-full items-center gap-2 md:min-w-0 md:flex-1"
          >
            <div className="relative flex-1">
              <Search
                size={18}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                aria-label="Išči lokacije"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => window.setTimeout(() => setIsSearchFocused(false), 120)}
                placeholder="Išči po regijah ali imenih..."
                className="w-full rounded-full border border-slate-200 bg-slate-100 py-3 pl-11 pr-10 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 md:pr-4"
              />
              <button
                type="submit"
                aria-label="Potrdi iskanje"
                className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-slate-900 text-white transition active:scale-95 md:hidden"
              >
                <Search size={14} />
              </button>

              {showSearchSuggestions ? (
                <div className="absolute left-0 right-0 top-[calc(100%+0.5rem)] overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-2xl ring-1 ring-slate-200">
                  {searchSuggestions.isLoading ? (
                    <div className="flex items-center gap-2 px-4 py-3 text-sm text-slate-500">
                      <Loader2 size={16} className="animate-spin" />
                      Iskanje predlogov…
                    </div>
                  ) : null}

                  {searchSuggestions.isError ? (
                    <div className="px-4 py-3 text-sm text-rose-600">
                      Predlogov ni bilo mogoče naložiti.
                    </div>
                  ) : null}

                  {!searchSuggestions.isLoading && !searchSuggestions.isError ? (
                    suggestionResults.length ? (
                      <div className="py-2">
                        {suggestionResults.map((item, index) => (
                          <SearchSuggestionItem
                            key={`${item.type}-${item.id ?? item.region_code ?? index}`}
                            item={item}
                            onSelect={handleSearchSuggestionSelect}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="px-4 py-3 text-sm text-slate-500">
                        Ni hitrih zadetkov. Poskusi oddati filter za širši seznam.
                      </div>
                    )
                  ) : null}
                </div>
              ) : null}
            </div>

            <input
              aria-label="Regija"
              value={regionInput}
              onChange={(event) => setRegionInput(event.target.value)}
              placeholder="Regija"
              className="hidden w-[140px] rounded-full border border-slate-200 bg-slate-100 px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:bg-white md:block"
            />

            <select
              aria-label="Opozorila"
              value={warningValue}
              onChange={(event) =>
                applySearchParams({ warning: event.target.value }, { replace: isHomePage })
              }
              className="hidden rounded-full border border-slate-200 bg-slate-100 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-emerald-400 focus:bg-white md:block"
            >
              <option value="">Vsa opozorila</option>
              {(warningTerms.data?.results ?? []).map((term: TaxonomyTerm) => (
                <option key={term.id} value={term.key}>
                  {term.label}
                </option>
              ))}
            </select>

            <button
              type="submit"
              className="hidden rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 md:block"
            >
              Filtriraj
            </button>
          </form>

          <div className="hidden items-center gap-3 md:ml-auto md:flex">
            <button
              type="button"
              onClick={handleForumClick}
              className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <MessageSquare size={18} />
              <span>Forum</span>
            </button>

            <button
              type="button"
              onClick={handleAddLocationClick}
              className="flex items-center gap-2 rounded-full bg-emerald-700 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800"
            >
              <Plus size={18} />
              <span>Dodaj lokacijo</span>
            </button>

            {authStatus.data?.authenticated ? (
              <div className="flex items-center gap-3 rounded-full border border-slate-200 bg-slate-50 px-4 py-2">
                <span className="text-sm font-medium text-slate-700">
                  {authStatus.data.user?.username}
                </span>
                <button
                  type="button"
                  onClick={() => logout.mutate()}
                  disabled={logout.isPending}
                  className="inline-flex items-center gap-2 text-sm text-slate-500 transition hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {logout.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
                  <span>Odjava</span>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setPostAuthRedirect(null)
                  setIsAuthModalOpen(true)
                }}
                className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <UserCircle size={18} />
                <span>Prijava</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <Outlet />

      {routeLocation.pathname === '/' ? (
        <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[450] hidden md:block">
          <div className="mx-auto max-w-md translate-y-0 pointer-events-auto lg:mx-0 lg:ml-6">
            <LegalFooter compact />
          </div>
        </div>
      ) : null}

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={handleAuthModalClose}
        onSuccess={handleAuthSuccess}
      />
      <CreateLocationModal
        isOpen={isCreateLocationModalOpen}
        onClose={() => setIsCreateLocationModalOpen(false)}
      />
    </div>
  )
}
